/**
 * SeityPanel — Configure the AI creative partner's personality and autonomy.
 *
 * Identity settings, blueprint conversation state, and CDE (Creator Development
 * Engine) configuration.
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from '@/api/engine';
import { PersonaNamePicker } from '@/components/settings/PersonaNamePicker';
import { SettingsSection, UpgradeNudge, DropdownSetting, ToggleSetting } from '@seitiate/settings-ui';
import { API_V1 } from '@/lib/env';
import { buildEngineHeaders } from '@/lib/auth';

interface AlterEgoSettings {
    autonomy_override: string | null;
    interaction_style: string;
    persona_name: string | null;
}

interface CDESettings {
    growth_edge_visibility: boolean;
    mirror_frequency: string;
}

interface BlueprintState {
    status: string;
    personality_traits?: string[];
    communication_style?: string;
}

export const SeityPanel: React.FC<{ mode: 'admin' | 'tenant' }> = () => {
    const [visibleCategories, setVisibleCategories] = useState<string[]>([]);
    const [alterEgo, setAlterEgo] = useState<AlterEgoSettings | null>(null);
    const [cde, setCDE] = useState<CDESettings | null>(null);
    const [blueprint, setBlueprint] = useState<BlueprintState | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [feedback, setFeedback] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const headers = buildEngineHeaders();
            const [overview, blueprintResp] = await Promise.all([
                api.settings.getOverview(),
                fetch(`${API_V1}/alter-ego/blueprint/state`, { headers }).catch(() => null),
            ]);
            setVisibleCategories(overview.visible_categories || []);

            const [alterEgoCategory, cdeCategory] = await Promise.all([
                overview.visible_categories.includes('alter_ego')
                    ? api.settings.getCategory('alter_ego').catch(() => null)
                    : Promise.resolve(null),
                overview.visible_categories.includes('cde')
                    ? api.settings.getCategory('cde').catch(() => null)
                    : Promise.resolve(null),
            ]);

            if (alterEgoCategory?.settings) setAlterEgo(alterEgoCategory.settings as unknown as AlterEgoSettings);
            if (cdeCategory?.settings) setCDE(cdeCategory.settings as unknown as CDESettings);

            if (blueprintResp?.ok) {
                setBlueprint(await blueprintResp.json());
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const updateAlterEgo = async (key: string, value: string | null) => {
        if (!alterEgo) return;
        setAlterEgo({ ...alterEgo, [key]: value });
        try {
            const response = await api.settings.updateCategory('alter_ego', { [key]: value || null });
            setAlterEgo(response.settings as unknown as AlterEgoSettings);
        } catch {
            setFeedback('Failed to save');
        }
    };

    const updateCDE = async (key: string, value: boolean | string) => {
        if (!cde) return;
        setCDE({ ...cde, [key]: value });
        try {
            const response = await api.settings.updateCategory('cde', { [key]: value });
            setCDE(response.settings as unknown as CDESettings);
        } catch {
            setFeedback('Failed to save');
        }
    };

    const startBlueprint = async () => {
        try {
            const resp = await fetch(`${API_V1}/alter-ego/blueprint/start`, {
                method: 'POST',
                headers: buildEngineHeaders(),
            });
            if (resp.ok) {
                setFeedback('Blueprint conversation started');
                await fetchData();
            }
        } catch {
            setFeedback('Failed to start blueprint');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-gray-500 font-mono text-sm animate-pulse">Loading seity config...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-red-400 font-mono text-sm">{error}</div>
            </div>
        );
    }

    const hasAlterEgo = visibleCategories.includes('alter_ego');
    const hasCDE = visibleCategories.includes('cde');

    return (
        <div className="space-y-4">
            <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-gray-300">
                Seity :: Creative Partner
            </h3>

            {feedback && (
                <div className="text-[10px] font-mono text-[#39FF14]">{feedback}</div>
            )}

            {/* Identity Settings */}
            {hasAlterEgo && alterEgo ? (
                <SettingsSection title="Identity" badge="MEANING+">
                    <PersonaNamePicker
                        initialName={alterEgo.persona_name || ''}
                        onSaved={() => {
                            void fetchData();
                        }}
                    />
                    <p className="text-[10px] text-gray-600">
                        Name is stored on the tenant record (canonical for the product). Category-backed copies refresh after
                        save.
                    </p>

                    <div className="border-t border-gray-800 pt-3 mt-3">
                        <div className="text-[9px] font-mono uppercase tracking-wider text-amber-400/70 mb-2">
                            Preview — saved for future use, not yet active
                        </div>
                    </div>

                    <DropdownSetting
                        label="Interaction Style"
                        value={alterEgo.interaction_style}
                        options={[
                            { value: 'scaffolded', label: 'Scaffolded (guided)' },
                            { value: 'balanced', label: 'Balanced' },
                            { value: 'autonomous', label: 'Autonomous' },
                        ]}
                        onChange={(v) => updateAlterEgo('interaction_style', v)}
                        disabled
                        description="How actively your seity engages in creative decisions"
                    />

                    <DropdownSetting
                        label="Autonomy Override"
                        value={alterEgo.autonomy_override || ''}
                        options={[
                            { value: '', label: 'Auto (from CDE profile)' },
                            { value: 'scaffolded', label: 'Scaffolded' },
                            { value: 'collaborative', label: 'Collaborative' },
                            { value: 'autonomous', label: 'Autonomous' },
                        ]}
                        onChange={(v) => updateAlterEgo('autonomy_override', v || null)}
                        disabled
                        description="Override the computed autonomy level, or leave on Auto"
                    />
                </SettingsSection>
            ) : (
                <UpgradeNudge requiredTier="Meaning" feature="seity identity" />
            )}

            {/* Blueprint Status */}
            <SettingsSection title="Blueprint" badge={blueprint?.status?.toUpperCase()}>
                <div className="bg-gray-900/40 rounded p-3 space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] font-mono text-gray-500">Status</span>
                        <span className={`text-[10px] font-mono ${
                            blueprint?.status === 'complete' ? 'text-[#39FF14]' :
                            blueprint?.status === 'in_progress' ? 'text-amber-400' :
                            'text-gray-500'
                        }`}>
                            {blueprint?.status || 'Not Started'}
                        </span>
                    </div>

                    {blueprint?.personality_traits && blueprint.personality_traits.length > 0 && (
                        <div>
                            <span className="text-[10px] font-mono text-gray-500 block mb-1">Personality</span>
                            <div className="flex flex-wrap gap-1">
                                {blueprint.personality_traits.map((trait) => (
                                    <span
                                        key={trait}
                                        className="text-[9px] font-mono px-1.5 py-0.5 bg-gray-800 text-gray-400 rounded"
                                    >
                                        {trait}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {blueprint?.communication_style && (
                        <div className="text-[10px] text-gray-500">
                            <span className="font-mono">Style:</span> {blueprint.communication_style}
                        </div>
                    )}
                </div>

                {(!blueprint || blueprint.status === 'not_started') && (
                    <button
                        onClick={startBlueprint}
                        className="px-3 py-1.5 text-xs font-mono bg-[#39FF14]/10 text-[#39FF14] border border-[#39FF14]/30 rounded hover:bg-[#39FF14]/20 transition-colors"
                    >
                        Start Blueprint Conversation
                    </button>
                )}
            </SettingsSection>

            {/* CDE Settings */}
            {hasCDE && cde ? (
                <SettingsSection title="Creator Development" badge="PREVIEW" badgeColor="text-amber-400">
                    <div className="text-[9px] font-mono text-amber-400/70 mb-1">
                        These controls are saved but not yet active. They will take effect in a future update.
                    </div>
                    <ToggleSetting
                        label="Growth Edge Visibility"
                        checked={cde.growth_edge_visibility}
                        onChange={(v) => updateCDE('growth_edge_visibility', v)}
                        disabled
                        description="Show growth edge suggestions in your Mirror reflections"
                    />
                    <DropdownSetting
                        label="Mirror Frequency"
                        value={cde.mirror_frequency}
                        options={[
                            { value: 'daily', label: 'Daily' },
                            { value: 'weekly', label: 'Weekly' },
                            { value: 'monthly', label: 'Monthly' },
                        ]}
                        onChange={(v) => updateCDE('mirror_frequency', v)}
                        disabled
                        description="How often you receive creative development reflections"
                    />
                </SettingsSection>
            ) : (
                <UpgradeNudge requiredTier="Meaning" feature="creator development settings" />
            )}
        </div>
    );
};

export const AlterEgoPanel = SeityPanel;
